import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCreateOrganization } from '../api/queries/organizations';
import { LookupSelect } from '../components/LookupSelect';
import { AddressFields } from '../components/AddressFields';
import { WebsiteInput } from '../components/WebsiteInput';
import {
  addressValueToColumns,
  emptyAddressValue,
  isAddressEmpty,
  type AddressValue,
} from '../utils/address';
import { normalizeWebsite } from '../utils/website';
import { ApiError } from '../api/client';
import type { OrganizationStatus } from '@stfors/shared-types';
import './NewClientPage.css';

export function NewOrganizationPage(): JSX.Element {
  const navigate = useNavigate();
  const create = useCreateOrganization();

  const [name, setName] = useState('');
  const [dbaName, setDbaName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [website, setWebsite] = useState('');
  const [ein, setEin] = useState('');
  const [industry, setIndustry] = useState('');
  const [address, setAddress] = useState<AddressValue>(emptyAddressValue);
  const [status, setStatus] = useState<OrganizationStatus>('active');
  const [notes, setNotes] = useState('');

  const [topError, setTopError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setTopError(null);
    setFieldErrors({});
    try {
      const res = await create.mutateAsync({
        name: name.trim(),
        dba_name: dbaName.trim() || undefined,
        email: email.trim() || undefined,
        phone: phone.trim() || undefined,
        website: normalizeWebsite(website) || undefined,
        ein: ein.trim() || undefined,
        industry: industry.trim() || undefined,
        // POST rejects null on these, so blanks are omitted entirely.
        ...(isAddressEmpty(address) ? {} : (() => {
          const cols = addressValueToColumns(address);
          return {
            address_line1: cols.line1 ?? undefined,
            address_line2: cols.line2 ?? undefined,
            city: cols.city ?? undefined,
            state: cols.state ?? undefined,
            postal_code: cols.postal_code ?? undefined,
            country: cols.country ?? undefined,
          };
        })()),
        notes: notes.trim() || undefined,
        status,
      });
      navigate(`/organizations/${res.organization.id}`);
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) {
        const body = err.body as { details?: { fieldErrors?: Record<string, string[]> } } | null;
        const fe = body?.details?.fieldErrors;
        if (fe) {
          const flat: Record<string, string> = {};
          for (const [k, v] of Object.entries(fe)) if (Array.isArray(v) && v.length > 0) flat[k] = v[0]!;
          setFieldErrors(flat);
          setTopError('Please fix the highlighted fields.');
          return;
        }
      }
      if (err instanceof ApiError && err.status === 409) {
        setTopError(err.message);
        return;
      }
      setTopError('Could not create the company. Please try again.');
    }
  };

  return (
    <section className="new-client-section">
      <header className="new-client-header">
        <h2>New Company</h2>
        <button type="button" className="btn-text" onClick={() => navigate('/directory')}>
          ← Back to directory
        </button>
      </header>

      <form className="new-client-form" onSubmit={onSubmit} noValidate>
        <div className="new-client-row">
          <label className="new-client-field">
            <span>Company name *</span>
            <input className="form-input" value={name} onChange={(e) => setName(e.target.value)} maxLength={200} required />
            {fieldErrors['name'] && <span className="field-error">{fieldErrors['name']}</span>}
          </label>
          <label className="new-client-field">
            <span>Doing business as</span>
            <input className="form-input" value={dbaName} onChange={(e) => setDbaName(e.target.value)} maxLength={200} />
          </label>
        </div>

        <div className="new-client-row">
          <label className="new-client-field">
            <span>Email</span>
            <input type="email" className="form-input" value={email} onChange={(e) => setEmail(e.target.value)} />
            {fieldErrors['email'] && <span className="field-error">{fieldErrors['email']}</span>}
          </label>
          <label className="new-client-field">
            <span>Phone</span>
            <input className="form-input" value={phone} onChange={(e) => setPhone(e.target.value)} />
            {fieldErrors['phone'] && <span className="field-error">{fieldErrors['phone']}</span>}
          </label>
        </div>

        <div className="new-client-row">
          <label className="new-client-field">
            <span>Website</span>
            <WebsiteInput value={website} onChange={setWebsite} disabled={create.isPending} />
          </label>
          <label className="new-client-field">
            <span>EIN</span>
            <input className="form-input" value={ein} onChange={(e) => setEin(e.target.value)} placeholder="XX-XXXXXXX" />
            {fieldErrors['ein'] && <span className="field-error">{fieldErrors['ein']}</span>}
          </label>
        </div>

        <div className="new-client-row">
          <label className="new-client-field">
            <span>Industry</span>
            <input className="form-input" value={industry} onChange={(e) => setIndustry(e.target.value)} maxLength={100} />
          </label>
          <label className="new-client-field">
            <span>Status</span>
            <LookupSelect category="organization_status" value={status} onChange={setStatus} className="form-input" aria-label="Status" />
          </label>
        </div>

        <div className="new-client-field new-client-field-full">
          <span>Address</span>
          <AddressFields
            value={address}
            onChange={setAddress}
            disabled={create.isPending}
            idPrefix="Company address"
          />
        </div>

        <label className="new-client-field new-client-field-full">
          <span>Notes</span>
          <textarea className="form-input new-client-textarea" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} maxLength={10000} />
        </label>

        {topError && <p className="new-client-error" role="alert">{topError}</p>}

        <div className="new-client-actions">
          <button type="button" className="btn-charcoal-secondary" onClick={() => navigate('/directory')} disabled={create.isPending}>
            Cancel
          </button>
          <button type="submit" className="btn-cyan-primary" disabled={create.isPending || name.trim().length === 0}>
            {create.isPending ? 'Creating…' : 'Create company'}
          </button>
        </div>
      </form>
    </section>
  );
}
